import React, { createContext, useCallback, useContext, useReducer } from 'react';

import { TemplateMeta } from 'models/templates/review';

import { REVIEW_QUESTION_TYPES } from 'constants/ProjectConstants';
import { insertAfterGivenPosition } from 'constants/helper';

const META = TemplateMeta.fromJSON({});

const PERFORMANCE = {
  section: REVIEW_QUESTION_TYPES.PERFORMANCE,
  templateQuestions: [],
};

const POTENTIAL = {
  section: REVIEW_QUESTION_TYPES.POTENTIAL,
  templateQuestions: [],
  competencyList: [],
};

const QNA = {
  section: REVIEW_QUESTION_TYPES.QNA,
  templateQuestions: [],
};

const defaultTemplateState = {
  META,
  PERFORMANCE,
  POTENTIAL,
  QNA,
};

const ReviewTemplateContext = createContext(defaultTemplateState);

const ACTIONS = {
  META: {
    SET_META: 'SET_META',
    CLEAR_META: 'CLEAR_META',
  },
  PERFORMANCE: {
    SET_PERFORMANCE_QUESTIONS: 'SET_PERFORMANCE_QUESTIONS',
    ADD_PERFORMANCE_QUESTION: 'ADD_PERFORMANCE_QUESTION',
    REMOVE_PERFORMANCE_QUESTION: 'REMOVE_PERFORMANCE_QUESTION',
    CLEAR_PERFORMANCE: 'CLEAR_PERFORMANCE',
  },
  POTENTIAL: {
    SET_POTENTIAL_QUESTIONS: 'SET_POTENTIAL_QUESTIONS',
    ADD_POTENTIAL_QUESTION: 'ADD_POTENTIAL_QUESTION',
    REMOVE_POTENTIAL_QUESTION: 'REMOVE_POTENTIAL_QUESTION',
    CLEAR_POTENTIAL: 'CLEAR_POTENTIAL',
  },
  QNA: {
    SET_QNA_QUESTIONS: 'SET_QNA_QUESTIONS',
    ADD_QNA_QUESTION: 'ADD_QNA_QUESTION',
    REMOVE_QNA_QUESTION: 'REMOVE_QNA_QUESTION',
    CLEAR_QNA: 'CLEAR_QNA',
  },
};

const metaReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case ACTIONS.META.SET_META:
      return {
        ...state,
        META: {
          ...state.META,
          ...payload.meta,
        },
      };
    case ACTIONS.META.CLEAR_META:
      return {
        ...state,
        META,
      };
    default:
      return state;
  }
};

const goalReducer = (state, action) => {
  const { type, payload } = action;
  const { index, question, questions, insertionPosition } = payload;

  switch (type) {
    case ACTIONS.PERFORMANCE.SET_PERFORMANCE_QUESTIONS:
      return {
        ...state,
        PERFORMANCE: {
          ...state.PERFORMANCE,
          templateQuestions: [...questions],
        },
      };

    case ACTIONS.PERFORMANCE.ADD_PERFORMANCE_QUESTION:
      return {
        ...state,
        PERFORMANCE: {
          ...state.PERFORMANCE,
          templateQuestions: insertAfterGivenPosition(
            state.PERFORMANCE.templateQuestions,
            insertionPosition,
            question
          ).map((ques, idx) => ({
            ...ques,
            displayOrder: idx + 1,
          })),
        },
      };
    case ACTIONS.PERFORMANCE.REMOVE_PERFORMANCE_QUESTION:
      return {
        ...state,
        PERFORMANCE: {
          ...state.PERFORMANCE,
          templateQuestions: [...state.PERFORMANCE.templateQuestions.filter((ques, idx) => idx !== index)],
        },
      };
    case ACTIONS.PERFORMANCE.CLEAR_PERFORMANCE:
      return {
        ...state,
        PERFORMANCE: {
          ...state.PERFORMANCE,
          templateQuestions: [],
        },
      };
    default:
      return state;
  }
};

const competencyReducer = (state, action) => {
  const { type, payload } = action;
  const { index, question, questions, competencyId, competencyList, insertionPosition } = payload;

  switch (type) {
    case ACTIONS.POTENTIAL.SET_POTENTIAL_QUESTIONS:
      return {
        ...state,
        POTENTIAL: {
          ...state.POTENTIAL,
          templateQuestions: { ...questions },
          competencyList: competencyList ? [...competencyList] : [...state.POTENTIAL.competencyList],
        },
      };

    case ACTIONS.POTENTIAL.ADD_POTENTIAL_QUESTION:
      return {
        ...state,
        POTENTIAL: {
          ...state.POTENTIAL,
          templateQuestions: {
            ...state.POTENTIAL.templateQuestions,
            [competencyId]: insertAfterGivenPosition(
              state.POTENTIAL.templateQuestions[competencyId],
              insertionPosition,
              question
            ).map((ques, idx) => ({
              ...ques,
              displayOrder: idx + 1,
            })),
          },
        },
      };
    case ACTIONS.POTENTIAL.REMOVE_POTENTIAL_QUESTION:
      return {
        ...state,
        POTENTIAL: {
          ...state.POTENTIAL,
          templateQuestions: {
            ...state.POTENTIAL.templateQuestions,
            [competencyId]: [...state.POTENTIAL.templateQuestions[competencyId].filter((ques, idx) => idx !== index)],
          },
        },
      };
    case ACTIONS.POTENTIAL.CLEAR_POTENTIAL:
      return {
        ...state,
        POTENTIAL: {
          ...state.POTENTIAL,
          templateQuestions: [],
        },
      };
    default:
      return state;
  }
};

const qnaReducer = (state, action) => {
  const { type, payload } = action;
  const { index, question, questions, insertionPosition } = payload;

  switch (type) {
    case ACTIONS.QNA.SET_QNA_QUESTIONS:
      return {
        ...state,
        QNA: {
          ...state.QNA,
          templateQuestions: [...questions],
        },
      };

    case ACTIONS.QNA.ADD_QNA_QUESTION:
      return {
        ...state,
        QNA: {
          ...state.QNA,
          templateQuestions: insertAfterGivenPosition(state.QNA.templateQuestions, insertionPosition, question).map(
            (ques, idx) => ({
              ...ques,
              displayOrder: idx + 1,
            })
          ),
        },
      };
    case ACTIONS.QNA.REMOVE_QNA_QUESTION:
      return {
        ...state,
        QNA: {
          ...state.QNA,
          templateQuestions: [...state.QNA.templateQuestions.filter((ques, idx) => idx !== index)],
        },
      };
    case ACTIONS.QNA.CLEAR_QNA:
      return {
        ...state,
        QNA: {
          ...state.QNA,
          templateQuestions: [],
        },
      };
    default:
      return state;
  }
};

const combinedReducer = (state, action) => {
  const { type } = action;
  if (ACTIONS.META[type]) {
    return metaReducer(state, action);
  }
  if (ACTIONS.PERFORMANCE[type]) {
    return goalReducer(state, action);
  }
  if (ACTIONS.POTENTIAL[type]) {
    return competencyReducer(state, action);
  }
  if (ACTIONS.QNA[type]) {
    return qnaReducer(state, action);
  }
  throw Error('Unknown Action Dispatched');
};

export const ReviewTemplateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(combinedReducer, defaultTemplateState);

  const setMeta = useCallback(({ meta }) => {
    dispatch({
      type: ACTIONS.META.SET_META,
      payload: { meta },
    });
  }, []);

  const clearMeta = useCallback(() => {
    dispatch({
      type: ACTIONS.META.CLEAR_META,
    });
  }, []);

  const setQuestions = useCallback(({ section, questions, competencyList /* Only for competency */ }) => {
    if (section === REVIEW_QUESTION_TYPES.PERFORMANCE) {
      dispatch({
        type: ACTIONS.PERFORMANCE.SET_PERFORMANCE_QUESTIONS,
        payload: { questions },
      });
    } else if (section === REVIEW_QUESTION_TYPES.POTENTIAL) {
      dispatch({
        type: ACTIONS.POTENTIAL.SET_POTENTIAL_QUESTIONS,
        payload: { questions, competencyList },
      });
    } else if (section === REVIEW_QUESTION_TYPES.QNA) {
      dispatch({
        type: ACTIONS.QNA.SET_QNA_QUESTIONS,
        payload: { questions },
      });
    } else {
      throw Error("You can't set questions of this section type.");
    }
  });

  const addQuestion = useCallback(
    ({ section, question, competencyId /* It'll be used only for POTENTIAL(Competency) */, insertionPosition }) => {
      if (section === REVIEW_QUESTION_TYPES.PERFORMANCE) {
        dispatch({
          type: ACTIONS.PERFORMANCE.ADD_PERFORMANCE_QUESTION,
          payload: { question, insertionPosition },
        });
      } else if (section === REVIEW_QUESTION_TYPES.POTENTIAL) {
        dispatch({
          type: ACTIONS.POTENTIAL.ADD_POTENTIAL_QUESTION,
          payload: { question, competencyId, insertionPosition },
        });
      } else if (section === REVIEW_QUESTION_TYPES.QNA) {
        dispatch({
          type: ACTIONS.QNA.ADD_QNA_QUESTION,
          payload: { question, insertionPosition },
        });
      } else {
        console.error("You can't add questions of this section type.");
      }
    }
  );

  const removeQuestion = useCallback(
    ({ section, index, competencyId /* It'll be used only for POTENTIAL(Competency) */ }) => {
      if (section === REVIEW_QUESTION_TYPES.PERFORMANCE) {
        dispatch({
          type: ACTIONS.PERFORMANCE.REMOVE_PERFORMANCE_QUESTION,
          payload: { index },
        });
      } else if (section === REVIEW_QUESTION_TYPES.POTENTIAL) {
        dispatch({
          type: ACTIONS.POTENTIAL.REMOVE_POTENTIAL_QUESTION,
          payload: { index, competencyId },
        });
      } else if (section === REVIEW_QUESTION_TYPES.QNA) {
        dispatch({
          type: ACTIONS.QNA.REMOVE_QNA_QUESTION,
          payload: { index },
        });
      } else {
        throw Error("You can't remove questions of this section type.");
      }
    }
  );

  return (
    <ReviewTemplateContext.Provider
      value={{
        state,
        setMeta,
        clearMeta,
        setQuestions,
        addQuestion,
        removeQuestion,
      }}
    >
      {children}
    </ReviewTemplateContext.Provider>
  );
};

export const useReviewTemplate = () => useContext(ReviewTemplateContext);
